import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { ContractNotification } from '../types';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export function useContractActions() {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const toggleFavorite = async (contractId: string) => {
    if (!user) {
      toast.error('Please sign in to favorite contracts');
      return;
    }

    try {
      setLoading(true);
      
      // Check if contract is already favorited
      const { data: existing, error: fetchError } = await supabase
        .from('contract_favorites')
        .select('id')
        .eq('contract_id', contractId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        // Remove favorite
        const { error } = await supabase
          .from('contract_favorites')
          .delete()
          .eq('contract_id', contractId)
          .eq('user_id', user.id);

        if (error) throw error;
        toast.success('Removed from favorites');
      } else {
        // Add favorite
        const { error } = await supabase
          .from('contract_favorites')
          .insert({ 
            contract_id: contractId,
            user_id: user.id
          });

        if (error) throw error;
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    } finally {
      setLoading(false);
    }
  };

  const setNotification = async (
    contractId: string,
    type: ContractNotification['type'],
    frequency: ContractNotification['frequency']
  ) => {
    if (!user) {
      toast.error('Please sign in to set notifications');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('contract_notifications')
        .upsert({
          contract_id: contractId,
          user_id: user.id,
          type,
          frequency
        }, {
          onConflict: 'user_id,contract_id,type'
        });

      if (error) throw error;
      toast.success('Notification settings updated');
    } catch (error) {
      console.error('Error setting notification:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  const removeNotification = async (
    contractId: string,
    type: ContractNotification['type']
  ) => {
    if (!user) {
      toast.error('Please sign in to manage notifications');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('contract_notifications')
        .delete()
        .match({ 
          contract_id: contractId,
          user_id: user.id,
          type 
        });

      if (error) throw error;
      toast.success('Notification removed');
    } catch (error) {
      console.error('Error removing notification:', error);
      toast.error('Failed to remove notification');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    toggleFavorite,
    setNotification,
    removeNotification
  };
}